import { useState } from 'react';
import CreatePinInput from '../CreatePin/CreatePinInput';

import { useTranslation } from "react-i18next";

const CreatePin = ({ onCreatePin, onSkip, loading = false }) => {
  const { t } = useTranslation("auth");
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({ 
    new_pin: '',
    confirm_pin: '' 
  });
  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };

      if (errors[field]) {
        setErrors(prevErrors => ({ ...prevErrors, [field]: '' }));
      }

      if (field === 'new_pin' && value.length === 4 && step === 1) {
        setTimeout(() => setStep(2), 300);
      } else if (field === 'confirm_pin' && value.length === 4 && step === 2) {
        handleSubmit(updated);
      }

      return updated;
    });
  };

  const handleSubmit = async (data) => {
    if (data.new_pin !== data.confirm_pin) {
      setErrors({ confirm_pin: t("PIN_DOESNT_MATCH") });
      setFormData(prev => ({ ...prev, confirm_pin: '' }));
      
      setTimeout(() => {
        setErrors({});
      }, 3000);
      return;
    }

    try {
      const success = await onCreatePin({ new_pin: data.new_pin });

      if (!success) {
        setFormData({ new_pin: '', confirm_pin: '' });
        setStep(1);
        setErrors({ new_pin: t("PIN_CREATE_ERR") });
        
        setTimeout(() => {
          setErrors({});
        }, 3000);
      }
    } catch (err) {
      console.error('CreatePin error:', err);
      setFormData({ new_pin: '', confirm_pin: '' });
      setStep(1);
      setErrors({ new_pin: t("JUST_ERR") });
      
      setTimeout(() => {
        setErrors({});
      }, 3000);
    }
  };

  const handleBackToCreate = () => {
    setStep(1);
    setFormData(prev => ({ ...prev, confirm_pin: '' }));
    setErrors({});
  };

  const handleSkip = () => {
    if (onSkip) {
      onSkip();
    }
  };

  return (
    <div className="fixed inset-0 bg-white flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-8 w-full max-w-md mx-auto shadow-2xl animate-fadeIn">
        
        {step === 1 ? (
          <CreatePinInput
            value={formData.new_pin}
            onChange={(value) => handleInputChange('new_pin', value)}
            error={errors.new_pin}
            disabled={loading}
            title={t("APROVE_NEW_PIN")}
            description={t("REPEAT_NEW_PIN")}
            icon={
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            }
          />
        ) : (
          <CreatePinInput
            value={formData.confirm_pin}
            onChange={(value) => handleInputChange('confirm_pin', value)}
            error={errors.confirm_pin}
            disabled={loading}
            title={t("APROVE_PIN")}
            description={t("REPEAT_PIN")}
            icon={
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            }
          />
        )}

        <div className="mt-8 space-y-3">
          {step === 1 && onSkip && (
            <button
              onClick={handleSkip}
              disabled={loading}
              className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-4 rounded-xl disabled:opacity-60 transition-all duration-200 text-sm border border-gray-200"
            >
              {t("SKIP_PIN_CREATE")}
            </button>
          )}

          {step === 2 && (
            <button
              onClick={handleBackToCreate}
              disabled={loading}
              className="w-full text-blue-500 font-medium py-3 hover:text-blue-600 disabled:opacity-60 transition-colors"
            >
              ← {t("CHANGE_PIN_CODE")}
            </button>
          )}
          
          <div className="bg-blue-50 rounded-lg p-4 mt-6">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <div className="text-sm font-medium text-blue-900 mb-1">
                  {step === 1 ? t("IMPORTANT_INFO") : t("ALMOST_READY") + '!'}
                </div>
                <div className="text-xs text-blue-700">
                  {step === 1 
                    ? t("PIN_MSG_1") + '.'
                    : t("PIN_MSG_2") + '.'
                  }
                </div>
              </div>
            </div>
          </div>
        </div>

        {loading && (
          <div className="absolute inset-0 bg-white bg-opacity-90 rounded-2xl flex items-center justify-center">
            <div className="flex flex-col items-center">
              <div className="w-8 h-8 border-3 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-4"></div>
              <p className="text-gray-600 font-medium">
                {step === 1 ? t("CREATE_PIN") + '...' : t("SAVING_PIN") + '...' }
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreatePin;